import { DocumentIcon, PlusIcon } from '@heroicons/react/24/outline';
import AuthGuard from '../components/AuthGuard';
import DocumentFilters from '../components/DocumentFilters';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../hooks/useAuth';
import { usePlatformData } from '../hooks/usePlatformData';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Button } from '../catalyst/button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DocumentsBulkSidebar } from './documents/DocumentsBulkSidebar';
import { DocumentsTableSection } from './documents/DocumentsTableSection';
import { useDocumentsList } from './documents/useDocumentsList';

export default function Documents() {
  const auth = useAuth();
  useDocumentTitle('Documents');
  const { categories } = usePlatformData();

  const {
    documents,
    loading,
    hasNextPage,
    setFilters,
    selectedIds,
    setSelectedIds,
    lastFetched,
    isDangerZoneExpanded,
    setIsDangerZoneExpanded,
    isDeleting,
    deleteError,
    selectedCategory,
    setSelectedCategory,
    isUpdatingCategory,
    categoryError,
    resetDepth,
    setResetDepth,
    isRetryingFailedSteps,
    setIsRetryingFailedSteps,
    fetchDocuments,
    handleResetSelected,
    handleDeleteSelected,
    confirmDialogProps,
    handleUpdateCategory,
    handleRemoveCategory,
    selectedDocumentsTags,
    loadMore,
    isResetting,
    resetError,
  } = useDocumentsList();

  return (
    <div className="">
      <PageHeader
        title="Documents"
        description="Manage and organize your documents"
        icon={<DocumentIcon className="size-6" />}
      >
        <div className="flex items-center gap-4 w-full justify-between md:justify-end">
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            {lastFetched && <>Last updated: {lastFetched.toLocaleTimeString()}</>}
          </p>
          <AuthGuard roles={['admin', 'user', 'curator']}>
            <Button color="indigo" href="/documents/upload">
              <PlusIcon className="size-5" />
              Upload New
            </Button>
          </AuthGuard>
        </div>
      </PageHeader>

      <DocumentFilters onFilterChange={setFilters} />

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className={`${selectedIds.size === 0 ? 'lg:col-span-12' : 'lg:col-span-8'}`}>
          <DocumentsTableSection
            loading={loading}
            hasNextPage={hasNextPage}
            documents={documents}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            loadMore={loadMore}
            showUserColumn={auth.hasAccess(['admin', 'user', 'curator'])}
          />
        </div>

        {selectedIds.size > 0 && (
          <DocumentsBulkSidebar
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            isUpdatingCategory={isUpdatingCategory}
            categoryError={categoryError}
            onUpdateCategory={handleUpdateCategory}
            onRemoveCategory={handleRemoveCategory}
            selectedDocumentsTags={selectedDocumentsTags}
            onTagsUpdated={fetchDocuments}
            isDangerZoneExpanded={isDangerZoneExpanded}
            setIsDangerZoneExpanded={setIsDangerZoneExpanded}
            resetDepth={resetDepth}
            setResetDepth={setResetDepth}
            onResetSelected={handleResetSelected}
            isResetting={isResetting}
            resetError={resetError}
            onDeleteSelected={handleDeleteSelected}
            isDeleting={isDeleting}
            deleteError={deleteError}
            isRetryingFailedSteps={isRetryingFailedSteps}
            setIsRetryingFailedSteps={setIsRetryingFailedSteps}
          />
        )}
      </div>

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
